import "globalthis/polyfill";
import { createRenderer, Config, loadFonts } from "@lightningtv/solid";
import { WebGlCoreRenderer, SdfTextRenderer } from "@lightningjs/renderer/webgl";
import { CanvasCoreRenderer, CanvasTextRenderer } from "@lightningjs/renderer/canvas";
import { Inspector } from "@lightningjs/renderer/inspector";
import { HashRouter, Route } from "@solidjs/router";
import { onMount } from "solid-js";
import App from "./pages/App";
import NotFound from "./pages/NotFound";
import fonts from "./fonts";
import LicenceScreen from "./pages/Licence";
import HomePage from "./pages/Home";
import Movies from "./pages/movies/Movies";
import Series from "./pages/series/Series";
import Entity from "./pages/Entity";
import LiveTvPage from "./pages/livetv/LIVETV";
import SearchPage from "./pages/search/search";
import SearchSeriesPage from "./pages/search/searchSeries";
import SearchLive from "./pages/search/searchLive";
import Player from "./pages/movies/playerMovie";
import SerieDetails from "./pages/SerieDetails";
import SettingsPage from "./pages/settings/Settings";
import AboutApp from "./pages/settings/AboutApp";
import UserInfo from "./pages/settings/UserInfo";
import SmartSearchPage from "./pages/search/smartSearchPage";
import { setIsVpnMode } from "../state";
import ServicesPage from "./pages/services/Services";
import * as Sentry from "@sentry/solid";
import { isLegacyWebView, useCanvasRenderer } from "./utils/legacyWebView";

// Old WebViews can't decode images in the renderer's image workers
// (XHR + createImageBitmap in a blob worker), so the font atlas and all
// images fail to load and the app stays on a black screen. Disable the workers
// and hide createImageBitmap so the renderer falls back to plain <img> loading.
// Must run before createRenderer() is called.
if (isLegacyWebView) {
  (window as any).createImageBitmap = undefined;
}

// Config settings
Config.debug = false;
Config.fontSettings.fontFamily = "Roboto";
Config.focusStateKey = "focus";
Config.fontSettings.color = 0xffffffff;
Config.rendererOptions = {
  numImageWorkers: isLegacyWebView ? 0 : 4,
  // Canvas2D fallback for boxes where Chrome blocks WebGL (see legacyWebView.ts)
  fontEngines: useCanvasRenderer ? [CanvasTextRenderer] : [SdfTextRenderer],
  renderEngine: useCanvasRenderer ? CanvasCoreRenderer : WebGlCoreRenderer,
  inspector: Inspector,
  deviceLogicalPixelRatio: window.innerHeight / 1080,
  devicePhysicalPixelRatio: 2,
  boundsMargin: 20,
};

console.log('Keto te dhenat ', window.innerHeight);


// Wrapped in try/catch: if Sentry throws on an old WebView it would abort this
// whole module and the app would never render. Session replay (rrweb) is too
// heavy for old boxes, so it is skipped there.
try {
Sentry.init({
  dsn: `https://4ae4f5cfac2af4056cc9b30c2b8cccab@o4510477149208576.ingest.de.sentry.io/4510477157335121`,
  integrations: isLegacyWebView
    ? [Sentry.browserTracingIntegration()]
    : [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  tracePropagationTargets: ["localhost"],

  // Capture Replay for 10% of all sessions,
  // plus 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});
} catch (e) {
  console.error("Sentry init failed:", e);
}

//@ts-ignore
if (import.meta.env.MODE === "production") {
  console.warn = () => { };
  console.log = () => { };
}

const root = document.getElementById("app");
//@ts-ignore
if (import.meta.env.DEV && !(root instanceof HTMLElement)) {
  throw new Error(
    "Root element not found. Did you forget to add it to your index.html? Or maybe the id attribute got misspelled?",
  );
}

// ========== CRITICAL: WAIT FOR SERVICE WORKER BEFORE RENDERING ==========
async function initializeServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('⚠️ Service Workers not supported');
    return;
  }

  try {
    // Step 1: Unregister ALL old service workers
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.unregister();
      console.log('🗑️ Unregistered old service worker');
    }

    // Step 2: Clear all caches
    const cacheNames = await caches.keys();
    for (const cacheName of cacheNames) {
      await caches.delete(cacheName);
      console.log('🗑️ Deleted cache:', cacheName);
    }

    // Step 3: Register new service worker
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/',
      updateViaCache: 'none' // Don't cache the service worker file
    });

    console.log('✅ Service Worker registered:', registration.scope);

    // Step 4: Wait for service worker to be ready and controlling
    await navigator.serviceWorker.ready;

    // Step 5: If there's a waiting worker, activate it
    if (registration.waiting) {
      console.log('⚡ Activating waiting service worker...');
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });

      // Wait for controller change
      await new Promise((resolve) => {
        navigator.serviceWorker.addEventListener('controllerchange', resolve, { once: true });
      });
    }

    // Step 6: Ensure we have an active controller
    if (!navigator.serviceWorker.controller) {
      console.log('🔄 No controller, reloading page...');
      window.location.reload();
      return;
    }

    console.log('✅ Service Worker is ready and controlling the page');
    console.log('🎮 Controller:', navigator.serviceWorker.controller);

  } catch (error) {
    console.error('❌ Service Worker initialization failed:', error);
  }
}

// ========== INITIALIZE APP ==========
async function initApp() {
  console.log('🚀 Initializing app...');

  // Wait for service worker FIRST, but never let it block rendering: on some
  // old Android WebViews `serviceWorker.ready` never resolves, which left the
  // app on a black screen forever.
  await Promise.race([
    initializeServiceWorker(),
    new Promise((resolve) => setTimeout(resolve, 4000)),
  ]);

  // Set VPN mode from localStorage
  const vpnStatus = localStorage.getItem("vpn");
  if (vpnStatus === "on") {
    setIsVpnMode(true);
    console.log('🔒 VPN mode enabled');
  } else {
    setIsVpnMode(false);
    console.log('🔓 VPN mode disabled');
  }

  console.log('✅ App initialized, starting render...');

  // Now render the app
  const { render } = createRenderer();
  // SDF fonts for WebGL, web fonts (fontUrl) for the Canvas2D fallback.
  loadFonts(fonts.filter((f) => ("fontUrl" in f) === useCanvasRenderer));
  render(
    () => (
      <HashRouter root={App}>
        <Route path="/" component={LicenceScreen} />
        <Route path="/home" component={HomePage} />
        <Route path="/movies" component={Movies} />
        <Route path="/series" component={Series} />
        <Route path="serieDetails/:type/:id/:category_id" component={SerieDetails} />
        <Route path="/livetv" component={LiveTvPage} />
        <Route path="/search" component={SearchPage} />
        <Route path="/searchseries" component={SearchSeriesPage} />
        <Route path="/searchlive" component={SearchLive} />
        <Route path="entity/:type/:id/:category_id" component={Entity} />
        <Route path="/player" component={Player} />
        <Route path="/settings" component={SettingsPage} />
        <Route path="/aboutApp" component={AboutApp} />
        <Route path="/userInfo" component={UserInfo} />
        <Route path="/smartSearch" component={SmartSearchPage} />
        <Route path="/services" component={ServicesPage} />
        <Route path="/*all" component={NotFound} />
      </HashRouter>
    ),
    //@ts-ignore
    root,
  );

  // Tells the boot watchdog in index.html that startup succeeded.
  (window as any).__appBooted = true;
  console.log('✅ App rendered successfully');
}

// Start the app
initApp().catch((e) => console.error("❌ App failed to start:", e));